import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  expensesAPI,
  getApiErrorMessage,
  type ExpenseOptionsDto,
  type ExpenseVoucherDto,
} from '@matix/api-client';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { loadExpenseOptions } from './useExpensesAvailable';
import './expenses.css';

/**
 * EXPENSES — the legacy till's expense voucher (سند صرف, Item/AddEditExpance),
 * ported. One voucher = one tender + one or more expense lines; the server
 * writes it through the ERP's own POS_AddEditExpance (GL voucher type 41 +
 * the drawer row), stamped with this till's shift. A CASH expense comes off
 * the shift's expected cash at close.
 *
 * Cancel exists only while the shift that paid it is still open — after the
 * close the count already includes it — and asks for a reason (audited).
 */
type Line = { key: number; typeId: number | ''; amount: string; note: string };
type View = 'shift' | 'today';

const todayKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

let lineSeq = 1;
const newLine = (): Line => ({ key: lineSeq++, typeId: '', amount: '', note: '' });

export function ExpensesScreen(): JSX.Element {
  const { t, lang } = useLang();
  const ar = lang === 'ar';
  const [options, setOptions] = useState<ExpenseOptionsDto | null>(null);
  const [tenderId, setTenderId] = useState<number | ''>('');
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>('shift');
  const [list, setList] = useState<ExpenseVoucherDto[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [cancelling, setCancelling] = useState<{ id: number; reason: string } | null>(null);

  useEffect(() => {
    void loadExpenseOptions(true).then((o) => {
      setOptions(o);
      const cash = o.tenders.find((x) => x.is_cash) ?? o.tenders[0];
      if (cash) setTenderId(cash.id);
    });
  }, []);

  const refresh = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await expensesAPI.list(view === 'shift' ? { shift: 'current' } : { from: todayKey(), to: todayKey() });
      setList(r.data);
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setLoadingList(false);
    }
  }, [view]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const typeName = (id: number | null, en: string | null, arName: string | null) =>
    (ar ? arName || en : en || arName) ?? (id != null ? `#${id}` : '—');
  const tender = options?.tenders.find((x) => x.id === tenderId) ?? null;

  const parsed = lines.map((l) => ({ ...l, value: Number(l.amount) }));
  const valid = parsed.filter((l) => l.typeId !== '' && Number.isFinite(l.value) && l.value > 0);
  const incomplete = parsed.some((l) => (l.typeId === '') !== (l.amount.trim() === '') || (l.amount.trim() !== '' && !(l.value > 0)));
  const total = valid.reduce((s, l) => s + l.value, 0);
  const canSave = !!tender && valid.length > 0 && !incomplete && !saving;

  const shiftTotals = useMemo(() => {
    const live = list.filter((v) => !v.cancelled);
    return {
      cash: live.filter((v) => v.is_cash).reduce((s, v) => s + v.amount, 0),
      other: live.filter((v) => !v.is_cash).reduce((s, v) => s + v.amount, 0),
      count: live.length,
    };
  }, [list]);

  const setLine = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const pickType = (key: number, value: string) => {
    const id = value === '' ? '' : Number(value);
    const type = options?.types.find((x) => x.id === id);
    setLines((ls) =>
      ls.map((l) =>
        l.key === key
          ? { ...l, typeId: id, amount: l.amount.trim() === '' && type && type.default_value > 0 ? String(type.default_value) : l.amount }
          : l,
      ),
    );
  };

  async function save() {
    if (!canSave || !tender) return;
    setSaving(true);
    try {
      const v = await expensesAPI.create({
        payment_type_id: tender.id,
        lines: valid.map((l) => ({ type_id: Number(l.typeId), amount: l.value, ...(l.note.trim() ? { note: l.note.trim() } : {}) })),
        ...(memo.trim() ? { note: memo.trim() } : {}),
      });
      toast(t({ en: `Expense voucher #${v.code} saved`, ar: `تم حفظ سند الصرف رقم ${v.code}` }), 'success');
      setLines([newLine()]);
      setMemo('');
      if (view !== 'shift') setView('shift');
      else void refresh();
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function confirmCancel() {
    if (!cancelling || cancelling.reason.trim().length < 2) return;
    try {
      await expensesAPI.cancel(cancelling.id, cancelling.reason.trim());
      toast(t({ en: 'Expense cancelled', ar: 'تم إلغاء المصروف' }), 'success');
      setCancelling(null);
      void refresh();
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    }
  }

  if (options && !options.available) {
    return (
      <div className="exp-screen">
        <div className="exp-empty-setup">
          {t({
            en: 'No expense types or expense payment methods are set up for this branch.',
            ar: 'لا توجد أنواع مصروفات أو طرق دفع للمصروفات معرّفة لهذا الفرع.',
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="exp-screen">
      <div className="exp-head">
        <div className="exp-title">{t({ en: 'Expenses', ar: 'المصروفات' })}</div>
        <div className="exp-sub">{t({ en: 'Expense voucher — paid from this till', ar: 'سند صرف — يُدفع من هذا الصندوق' })}</div>
      </div>

      <div className="exp-body">
        {/* ── New voucher ── */}
        <section className="exp-card exp-form" aria-label={t({ en: 'New expense', ar: 'مصروف جديد' })}>
          <h3>{t({ en: 'New expense voucher', ar: 'سند صرف جديد' })}</h3>

          <label className="exp-field">
            <span>{t({ en: 'Paid with', ar: 'طريقة الدفع' })}</span>
            <select value={tenderId} onChange={(e) => setTenderId(e.target.value === '' ? '' : Number(e.target.value))}>
              {(options?.tenders ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {(ar ? x.name_ar || x.name : x.name) + (x.is_cash ? '' : '')}
                </option>
              ))}
            </select>
            {tender?.is_cash && (
              <small className="exp-hint">
                {t({ en: 'Cash leaves the drawer — it comes off this shift’s expected cash.', ar: 'النقد يخرج من الدرج — ويُخصم من النقد المتوقع لهذه الوردية.' })}
              </small>
            )}
          </label>

          <div className="exp-lines">
            <div className="exp-line exp-line--head" aria-hidden>
              <span>{t({ en: 'Expense', ar: 'المصروف' })}</span>
              <span>{t({ en: 'Amount', ar: 'المبلغ' })}</span>
              <span>{t({ en: 'Note', ar: 'ملاحظة' })}</span>
              <span />
            </div>
            {lines.map((l) => (
              <div className="exp-line" key={l.key}>
                <select aria-label={t({ en: 'Expense type', ar: 'نوع المصروف' })} value={l.typeId} onChange={(e) => pickType(l.key, e.target.value)}>
                  <option value="">{t({ en: '— choose —', ar: '— اختر —' })}</option>
                  {(options?.types ?? []).map((x) => (
                    <option key={x.id} value={x.id}>{typeName(x.id, x.name, x.name_ar)}</option>
                  ))}
                </select>
                <input
                  aria-label={t({ en: 'Amount', ar: 'المبلغ' })}
                  inputMode="decimal"
                  className="exp-amount"
                  value={l.amount}
                  onChange={(e) => setLine(l.key, { amount: e.target.value.replace(/[^\d.]/g, '') })}
                  placeholder="0"
                />
                <input
                  aria-label={t({ en: 'Note', ar: 'ملاحظة' })}
                  value={l.note}
                  maxLength={500}
                  onChange={(e) => setLine(l.key, { note: e.target.value })}
                />
                <button
                  type="button"
                  className="exp-x"
                  title={t({ en: 'Remove line', ar: 'حذف السطر' })}
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="exp-add" onClick={() => setLines((ls) => [...ls, newLine()])} disabled={lines.length >= 20}>
              + {t({ en: 'Add line', ar: 'إضافة سطر' })}
            </button>
          </div>

          <label className="exp-field">
            <span>{t({ en: 'Notes', ar: 'ملاحظات' })}</span>
            <textarea rows={2} maxLength={500} value={memo} onChange={(e) => setMemo(e.target.value)} />
          </label>

          <div className="exp-total">
            <span>{t({ en: 'Total', ar: 'الإجمالي' })}</span>
            <strong>{fmt(total)}</strong>
          </div>
          {incomplete && (
            <div className="exp-warn">{t({ en: 'Each line needs both an expense and an amount.', ar: 'كل سطر يحتاج نوع مصروف ومبلغاً.' })}</div>
          )}
          <button type="button" className="exp-save" disabled={!canSave} onClick={() => void save()}>
            {saving ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Save expense', ar: 'حفظ السند' })}
          </button>
        </section>

        {/* ── Vouchers ── */}
        <section className="exp-card exp-list" aria-label={t({ en: 'Expense vouchers', ar: 'سندات الصرف' })}>
          <div className="exp-list-head">
            <div className="exp-tabs" role="tablist">
              <button role="tab" aria-selected={view === 'shift'} className={view === 'shift' ? 'is-on' : ''} onClick={() => setView('shift')}>
                {t({ en: 'This shift', ar: 'هذه الوردية' })}
              </button>
              <button role="tab" aria-selected={view === 'today'} className={view === 'today' ? 'is-on' : ''} onClick={() => setView('today')}>
                {t({ en: 'Today (branch)', ar: 'اليوم (الفرع)' })}
              </button>
            </div>
            <div className="exp-sums">
              <span>{t({ en: 'Cash', ar: 'نقداً' })} <strong>{fmt(shiftTotals.cash)}</strong></span>
              {shiftTotals.other > 0 && (
                <span>{t({ en: 'Other', ar: 'أخرى' })} <strong>{fmt(shiftTotals.other)}</strong></span>
              )}
            </div>
          </div>

          <div className="exp-items">
            {loadingList && list.length === 0 ? (
              <div className="exp-empty">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
            ) : list.length === 0 ? (
              <div className="exp-empty">{t({ en: 'No expenses yet.', ar: 'لا توجد مصروفات بعد.' })}</div>
            ) : (
              list.map((v) => (
                <div key={v.id} className={`exp-item${v.cancelled ? ' is-cancelled' : ''}`}>
                  <div className="exp-item-top">
                    <strong>#{v.code}</strong>
                    <span className="exp-muted">
                      {v.created_at ? new Date(v.created_at).toLocaleTimeString(ar ? 'ar' : 'en', { hour: '2-digit', minute: '2-digit' }) : ''}
                      {' · '}
                      {ar ? v.payment_type_name_ar || v.payment_type_name : v.payment_type_name}
                    </span>
                    <span className="exp-amount-big">{fmt(v.amount)}</span>
                  </div>
                  <div className="exp-item-lines">
                    {v.lines.map((l, i) => (
                      <span key={i}>
                        {typeName(l.type_id, l.type_name, l.type_name_ar)} {fmt(l.amount)}
                        {l.note && l.note !== v.memo ? ` — ${l.note}` : ''}
                      </span>
                    ))}
                  </div>
                  {v.memo && <div className="exp-muted">{v.memo}</div>}
                  <div className="exp-item-foot">
                    <span className="exp-muted">{v.created_by_name ?? ''}</span>
                    {v.cancelled ? (
                      <span className="exp-badge">{t({ en: 'Cancelled', ar: 'ملغى' })}</span>
                    ) : v.cancellable ? (
                      cancelling?.id === v.id ? (
                        <span className="exp-cancel-row">
                          <input
                            autoFocus
                            placeholder={t({ en: 'Reason', ar: 'السبب' })}
                            value={cancelling.reason}
                            maxLength={300}
                            onChange={(e) => setCancelling({ id: v.id, reason: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && void confirmCancel()}
                          />
                          <button type="button" className="exp-danger" disabled={cancelling.reason.trim().length < 2} onClick={() => void confirmCancel()}>
                            {t({ en: 'Cancel it', ar: 'إلغاء' })}
                          </button>
                          <button type="button" onClick={() => setCancelling(null)}>{t({ en: 'Keep', ar: 'إبقاء' })}</button>
                        </span>
                      ) : (
                        <button type="button" className="exp-link" onClick={() => setCancelling({ id: v.id, reason: '' })}>
                          {t({ en: 'Cancel', ar: 'إلغاء' })}
                        </button>
                      )
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
